import { definePreset } from "@primeuix/themes";
import Aura from "@primeuix/themes/aura";

// Palette from the "Visual consistency – Style guide" board.
// Primary = teal-green (clickable), surfaces = primeng blue-gray (Tailwind slate),
// AI = orange, danger = red.

export const primary = {
  50: "#e6f5f2",
  100: "#c0e7e0",
  200: "#97d8cd",
  300: "#6dc8b9",
  400: "#4dbcaa",
  500: "#07a190", // Primary
  600: "#098174", // Primary-text / clicable text
  700: "#006867", // Primary hover
  800: "#045049",
  900: "#06403b",
  950: "#032420",
};

// primeng blue-gray ramp (matches board's "Blue gray primeng pallete")
export const surface = {
  0: "#ffffff",
  50: "#f9fafb", // card background gray-50
  100: "#f1f5f9", // surface light blue-gray-100
  200: "#e2e8f0", // borders / editing fields blue-gray-200
  300: "#cbd5e1", // border dark
  400: "#94a3b8",
  500: "#64748b", // light text blue-gray-500
  600: "#475569",
  700: "#334155",
  800: "#1e293b", // default text blue-gray-800
  900: "#0f172a",
  950: "#020617",
};

// Tailwind sky ramp — notification accents (`$sky-*` in _variables.scss) and
// the water on the potential map.
export const sky = {
  100: "#e0f2fe",
  200: "#bae6fd",
  600: "#0284c7",
};

export const QuotissPreset = definePreset(Aura, {
  primitive: {
    // Single 8px radius (mirrors _radius.scss) — controls and surfaces alike.
    // Aura ships sm 4px / lg 8px / xl 12px; flatten every step the components
    // reference so nothing needs a per-component override. `xs` stays a separate
    // 4px step for corners nested inside an already-rounded control (chips in a
    // select, toggle buttons in a select button, checkboxes).
    borderRadius: {
      xs: "4px",
      sm: "8px",
      md: "8px",
      lg: "8px",
      xl: "8px",
    },
    // 8-point spacing grid — mirrors _spacing.scss. Keep the two in sync;
    // referenced below as {spacing.*} so no component carries a literal.
    spacing: {
      4: "4px",
      8: "8px",
      16: "16px",
      24: "24px",
      32: "32px",
      40: "40px",
      48: "48px",
    },
    // Type scale — mirrors _type.scss. Keep the two in sync; referenced below
    // as {font.size.*} / {font.weight.*} so no component carries a literal.
    fontSize: {
      "2xs": "0.625rem", // 10px
      xs: "0.75rem", // 12px
      sm: "0.8125rem", // 13px
      base: "0.875rem", // 14px
      md: "1rem", // 16px
      lg: "1.125rem", // 18px
      xl: "1.25rem", // 20px
      "2xl": "1.5rem", // 24px
      "3xl": "2rem", // 32px
      "4xl": "3rem", // 48px
    },
    // Only these four weights ship in _roboto.scss.
    fontWeight: {
      light: "300",
      regular: "400",
      medium: "500",
      bold: "700",
    },
    // AI elements (orange) and danger (red) exposed as primitives for reuse
    ai: {
      background: "#ffe1ce", // orange-glow 25%
      color: "#dd5d00", // AI text / icons
    },
    danger: {
      background: "#fef2f2", // red-50
      border: "#cbd5e1",
      color: "#b91c1c", // red-700
    },
  },
  semantic: {
    primary,
    // Global form-field radius/border — applies to every input-like component
    // (inputtext, select, multiselect, autocomplete, datepicker input, textarea…).
    // NB: padding is intentionally NOT set here. Control HEIGHT is not derived
    // from padding at all — `_controls.scss` owns the 40px/32px rail. The
    // inherited Aura paddingY survives only for the components that read it for
    // something other than height (multiselect chip spacing, textarea).
    formField: {
      borderRadius: "{border.radius.md}", // 8px — controls
    },
    // Surfaces (card, panel, accordion, datatable, tabs). Same 8px as controls;
    // kept explicit so the two can be split again without hunting components.
    content: {
      borderRadius: "{border.radius.lg}", // 8px
    },
    colorScheme: {
      light: {
        surface,
        primary: {
          color: "{primary.500}",
          contrastColor: "#ffffff",
          hoverColor: "{primary.700}",
          activeColor: "{primary.700}",
        },
        text: {
          color: "{surface.800}", // default text #1e293b
          mutedColor: "{surface.500}", // light text #64748b
        },
        content: {
          background: "{surface.0}",
          borderColor: "{surface.200}", // #e2e8f0
        },
        formField: {
          background: "{surface.0}",
          borderColor: "{surface.200}", // #e2e8f0 — System/Surfaces/Border, global input border
          color: "{surface.800}",
          focusBorderColor: "{primary.500}",
        },
      },
    },
  },
  components: {
    checkbox: {
      root: {
        // Unchecked box needs a darker border than the global form-field
        // #e2e8f0 to stay visible on white — style guide says blue-gray-500.
        borderColor: "{surface.500}", // #64748b
        borderRadius: "{border.radius.xs}", // 4px
      },
    },
    autocomplete: {
      root: {
        // The multiple-value box pads off `padding.y / 2`, not `padding.y`, so
        // paddingY is written as the doubled grid step to land on the 4px gap
        // between chips. `padding.x` is deliberately left at the Aura form-field
        // default (10px): it is the inline padding of the box while it holds no
        // chip, so pulling it down to the chip gap misaligns the placeholder
        // against every other input. The chip state overrides it anyway, via
        // the `:has(.p-autocomplete-chip)` rule.
        paddingY: "{spacing.4} * 2",
      },
      // Aura fills the dropdown trigger with surface.100; render it like the
      // p-select chevron instead — field background, form-field icon color.
      dropdown: {
        background: "{form.field.background}",
        hoverBackground: "{form.field.background}",
        activeBackground: "{form.field.background}",
        color: "{form.field.icon.color}",
        hoverColor: "{form.field.icon.color}",
        activeColor: "{form.field.icon.color}",
      },
      chip: {
        // Same 8px default as a standalone chip. The step down to 4px is not a
        // token — it is the "chip inside a box" rule in ng-prime/_other.scss.
        borderRadius: "{border.radius.sm}", // 8px
      },
    },
    radiobutton: {
      root: {
        // Match the checkbox: darker border than the form-field default so the
        // unchecked radio stays visible on white.
        borderColor: "{surface.500}", // #64748b
      },
    },
    chip: {
      root: {
        // Aura ships 1rem here — pull it back to the checkbox radius.
        borderRadius: "{border.radius.sm}", // 8px
        paddingX: "{spacing.8}",
        paddingY: "{spacing.4}",
        gap: "{spacing.8}", // breathing room around the remove icon
      },
      label: {
        fontWeight: "{font.weight.medium}",
        fontSize: "{font.size.base}", // 14px
      },
      removeIcon: {
        color: "{primary.600}", // green #098174 — close/times icon
        size: "0.75rem", // 12px
      },
    },
    button: {
      root: {
        paddingX: "16px",
        gap: "0.375rem", // 6px icon↔label
        label: {
          fontWeight: "{font.weight.regular}",
        },
        sm: {
          fontSize: "{font.size.sm}", // 13px
          paddingX: "0.75rem", // 12px
          // No paddingY — the small rail in `_controls.scss` owns the height.
        },
      },
      text: {
        primary: {
          color: "{primary.600}", // clickable-text green #098174
        },
        danger: {
          // Aura ships red-500 here; align with the $danger scss var so
          // hand-written styles and PrimeNG buttons render the same red.
          color: "{danger.color}", // #b91c1c
        },
        warn: {
          color: "#a25201", // Color/Yellow-text
          hoverBackground: "#fff7e7", // Color/Yellow-light
          activeBackground: "#fff7e7",
        },
      },
      outlined: {
        primary: {
          // Aura outlines primary with primary.200 — far too light against
          // white. Style guide: primary.500 border, primary.600 label.
          borderColor: "{primary.500}",
          color: "{primary.600}",
          hoverBackground: "{primary.50}",
          activeBackground: "{primary.100}",
        },
        warn: {
          borderColor: "#e49b0a", // Color/yellow-dark
          color: "#a25201", // Color/Yellow-text
          hoverBackground: "transparent",
          activeBackground: "transparent",
        },
      },
    },
    togglebutton: {
      root: {
        padding: "0 20px",
        gap: "8px",
        // Same 14px as inputs, chips and filters — the segmented control sits
        // in the same rail. The frame is a fixed 40px either way, so
        // `size="small"` must not drop the label to Aura's 12px — one segmented
        // control, one label size.
        fontSize: "{font.size.base}", // 14px
        sm: { fontSize: "{font.size.base}" },
        fontWeight: "{font.weight.regular}",
        background: "{surface.0}",
        hoverBackground: "{surface.0}",
        borderColor: "transparent",
        color: "{surface.500}", // muted #64748b
        checkedBackground: "#dbf2ee", // surfaces/active-transparent
        checkedBorderColor: "#a3cdce", // surfaces/active
        checkedColor: "{surface.800}", // default text #1e293b
      },
      content: {
        // Aura fills the content span on the checked state ("inset card"); clear
        // it so the root's mint active-transparent background shows through.
        checkedBackground: "transparent",
        checkedShadow: "none",
      },
    },
    datepicker: {
      dropdown: {
        // calendar trigger button — surfaces/active-transparent bg, primary-hover icon
        background: "#dbf2ee",
        hoverBackground: "{primary.100}",
        activeBackground: "{primary.200}",
        color: "{primary.700}", // #006867
        hoverColor: "{primary.700}",
        activeColor: "{primary.700}",
      },
    },
    select: {
      list: {
        header: {
          padding: "0.25rem 0.5rem",
        },
      },
    },
    multiselect: {
      list: {
        header: {
          // Inline start is list padding (0.25rem) + option padding (0.625rem)
          // so the select-all checkbox sits on the same rail as the option
          // checkboxes below it — an alignment constant, not a spacing step.
          padding: "0.5rem 0.5rem 0.25rem 0.875rem",
        },
      },
    },
    datatable: {
      paginatorBottom: {
        borderWidth: "0",
      },
    },
    paginator: {
      root: {
        gap: "0.5rem", // 8px between controls
      },
      navButton: {
        width: "2.625rem", // 42px
        height: "2.625rem",
        fontSize: "{font.size.base}", // 14px
        selectedBackground: "#dbf2ee", // surfaces/active-transparent
        selectedColor: "{text.color}", // default-text #1e293b
      },
      currentPageReport: {
        fontSize: "{font.size.sm}", // 13px
        fontWeight: "{font.weight.light}",
      },
    },
  },
});
